import { useCallback, useEffect, useState } from 'react'

import { getJivoSiteConfig } from '@shared/lib/jivosite'

declare global {
  interface Window {
    jivo_api?: {
      open: () => void;
      close: () => void;
      setContactInfo: (info: { 
        name?: string;
        email?: string; 
        phone?: string;
        description?: string;
      }) => void;
      onReady?: (callback: () => void) => void;
      show?: () => void;
      hide?: () => void;
    };
    jivo_config?: {
      widget_id: string;
      hide_on_load?: boolean;
      hide_widget?: boolean;
    };
    jivo_init?: boolean;
  }
}

export const useJivoSite = () => {
  const [ isReady, setIsReady ] = useState(false)
  const config = getJivoSiteConfig()

  useEffect(() => {
    // Проверяем доступность JivoSite API
    const checkJivoReady = () => {
      if (window.jivo_api) {
        setIsReady(true)
        if (window.jivo_api.onReady) {
          window.jivo_api.onReady(() => {
            setIsReady(true)
          })
        }
      }
      else {
        // Повторная проверка через 100мс
        setTimeout(checkJivoReady, 100)
      }
    }

    // Начинаем проверку только если Widget ID настроен
    if (config.widgetId) {
      checkJivoReady()
    }
  }, [ config.widgetId ])

  const openJivoChat = useCallback(() => {
    if (!config.widgetId) {
      console.warn('JivoSite Widget ID not configured')
      return false
    }

    if (window.jivo_api) {
      // Показываем виджет если он был скрыт
      if (window.jivo_api.show) {
        window.jivo_api.show()
      }
      // Открываем чат
      window.jivo_api.open()
      return true
    }
    else {
      console.warn('JivoSite API not available')
      return false
    }
  }, [ config.widgetId ])

  const closeJivoChat = useCallback(() => {
    if (window.jivo_api) {
      window.jivo_api.close()
      // Скрываем виджет после закрытия
      if (window.jivo_api.hide) {
        window.jivo_api.hide()
      }
      return true
    }
    return false
  }, [])

  const showWidget = useCallback(() => {
    if (window.jivo_api?.show) {
      window.jivo_api.show()
      return true
    }
    return false
  }, [])

  const hideWidget = useCallback(() => {
    if (window.jivo_api?.hide) {
      window.jivo_api.hide()
      return true
    }
    return false
  }, [])

  const setContactInfo = useCallback((info: { 
    name?: string;
    email?: string; 
    phone?: string;
    description?: string;
  }) => {
    if (window.jivo_api) {
      window.jivo_api.setContactInfo(info)
      return true
    }
    return false
  }, [])

  return {
    openJivoChat,
    closeJivoChat,
    showWidget,
    hideWidget,
    setContactInfo,
    isReady: isReady && !!config.widgetId,
    widgetId: config.widgetId,
  }
}
